import { isAxiosError, type AxiosRequestConfig } from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../hook_au/useAuth';
import apiClient from './apiClient';
import type { DashboardAlertsResponse, DashboardOverviewResponse } from '../types/dashboard';

type RequestConfig<Response> = AxiosRequestConfig & { skipAuth?: boolean };

const ERROR_MESSAGES = {
  unauthorized: 'La sesión expiró. Vuelve a iniciar sesión para continuar.',
  alerts: 'No pudimos cargar las alertas del dashboard. Inténtalo de nuevo en unos segundos.',
  overview: 'No pudimos cargar el resumen del dashboard. Inténtalo de nuevo en unos segundos.',
} as const;

const EMPTY_ALERTS_RESPONSE: DashboardAlertsResponse = {
  items: [],
  summary: { total: 0, orders: 0, subprojects: 0 },
};

const EMPTY_OVERVIEW_RESPONSE: DashboardOverviewResponse = {
  summary: {
    active_projects: 0,
    in_progress_subprojects: 0,
    open_orders: 0,
    active_alerts: 0,
  },
  projects_by_status: [],
  orders_by_status: [],
  items_by_state: [],
  subprojects_to_watch: {
    cost_deviation: [],
    pending_orders: [],
  },
};

function useDashboardRequest() {
  const { token } = useAuth();

  const request = useCallback(
    async <Response>(config: RequestConfig<Response>): Promise<Response> => {
      if (!token && !config.skipAuth) {
        throw new Error('No hay sesión activa para llamar a la API de dashboard');
      }
      try {
        const response = await apiClient.request<Response>({
          ...config,
          url: `/dashboard${config.url ?? ''}`,
        });
        return response.data;
      } catch (err) {
        if (isAxiosError(err) && err.response?.status === 401) {
          throw new Error(ERROR_MESSAGES.unauthorized);
        }
        throw err;
      }
    },
    [token],
  );

  return { token, request };
}

export function useDashboardAlerts() {
  const { token, request } = useDashboardRequest();
  const [data, setData] = useState<DashboardAlertsResponse>(EMPTY_ALERTS_RESPONSE);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await request<DashboardAlertsResponse>({
        method: 'GET',
        url: '/alerts',
      });
      setData(response);
      return response;
    } catch (err) {
      const message = isAxiosError(err) && err.response?.status === 401 ? ERROR_MESSAGES.unauthorized : ERROR_MESSAGES.alerts;
      setError(message);
      throw new Error(message);
    } finally {
      setLoading(false);
    }
  }, [request]);

  useEffect(() => {
    if (!token) {
      setData(EMPTY_ALERTS_RESPONSE);
      return;
    }
    void refresh();
  }, [token, refresh]);

  return {
    data,
    loading,
    error,
    refresh,
  };
}

export function useDashboardOverview() {
  const { token, request } = useDashboardRequest();
  const [data, setData] = useState<DashboardOverviewResponse>(EMPTY_OVERVIEW_RESPONSE);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await request<DashboardOverviewResponse>({
        method: 'GET',
        url: '/overview',
      });
      setData(response);
      return response;
    } catch (err) {
      const message = isAxiosError(err) && err.response?.status === 401 ? ERROR_MESSAGES.unauthorized : ERROR_MESSAGES.overview;
      setError(message);
      throw new Error(message);
    } finally {
      setLoading(false);
    }
  }, [request]);

  useEffect(() => {
    if (!token) {
      setData(EMPTY_OVERVIEW_RESPONSE);
      return;
    }
    void refresh();
  }, [token, refresh]);

  return {
    data,
    loading,
    error,
    refresh,
  };
}
